import { View, Text, hexColor } from "@lightningtv/solid";
import { createSignal, onMount } from "solid-js";
import { fontFamily, isRTL, t } from "../../../translations/configuration";
import { STREAM_EXTENSION_STORAGE_KEY } from "../../../../state";

type ExtensionType = ".ts" | ".m3u8";

interface StreamExtensionProps {
    x?: number;
    tsRef: (el: any) => void;
    m3u8Ref: (el: any) => void;
    onLeftFromOption: () => void;
    onRightFromOption: () => void;
}

const StreamExtension = (props: StreamExtensionProps) => {
    let tsEl: any;
    let m3u8El: any;

    const [selected, setSelected] = createSignal<ExtensionType>(".ts");

    onMount(() => {
        const saved = localStorage.getItem(STREAM_EXTENSION_STORAGE_KEY) as ExtensionType | null;
        if (saved === ".ts" || saved === ".m3u8") {
            setSelected(saved);
        }
    });

    const choose = (ext: ExtensionType) => {
        setSelected(ext);
        localStorage.setItem(STREAM_EXTENSION_STORAGE_KEY, ext);
        console.log("Stream extension set to:", ext);
    };

    const optionStyle = (ext: ExtensionType) => ({
        width: 420,
        height: 76,
        borderRadius: 12,
        color: hexColor("#1a1d24"),
        border: {
            width: selected() === ext ? 2 : 1,
            color: hexColor("#374151"),
        },
        focus: {
            color: hexColor("#1e3a5f68"),
            border: { width: 2, color: hexColor("#326dcf") },
        },
        transition: { color: { duration: 150 } },
    });

    return (
        <View x={props.x ?? 600} y={80} height={900} width={920} clipping>

            {/* Title */}
            <Text
                width={880}
                contain="width"
                textAlign={isRTL() ? "right" : "left"}
                style={{
                    fontSize: 32,
                    color: hexColor("#f0f6fc"),
                    fontWeight: isRTL() ? "normal" : "bold",
                    fontFamily: fontFamily(),
                }}>
                {t("stream_extension")}
            </Text>

            {/* Description */}
            <Text y={54}
                textAlign={isRTL() ? "right" : "left"}
                style={{
                    fontSize: 22,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                    width: 880,
                    contain: "width",
                    lineHeight: 34,
                }}>
                {t("stream_extension_desc")}
            </Text>

            {/* Option: .ts */}
            <View
                ref={(el) => { tsEl = el; props.tsRef(el); }}
                x={isRTL() ? 450 : 0}
                y={136}
                style={optionStyle(".ts")}
                onDown={() => m3u8El?.setFocus()}
                onLeft={() => props.onLeftFromOption()}
                onRight={() => props.onRightFromOption()}
                onEnter={() => choose(".ts")}
            >
                {/* Radio circle — mirrors in RTL */}
                <View
                    x={isRTL() ? 372 : 24} y={26} width={24} height={24} borderRadius={12}
                    style={{
                        color: hexColor("#374151"),
                        border: { width: 2, color: hexColor("#6b7280") },
                    }}
                >
                    {selected() === ".ts" && (
                        <View x={6} y={6} width={12} height={12} borderRadius={6}
                            style={{ color: hexColor("#ffffff") }}
                        />
                    )}
                </View>

                <Text
                    x={isRTL() ? 10 : 64} y={16}
                    width={340}
                    contain="width"
                    textAlign={isRTL() ? "right" : "left"}
                    style={{
                        fontSize: 26,
                        color: selected() === ".ts" ? hexColor("#f0f6fc") : hexColor("#9ca3af"),
                        fontWeight: isRTL() ? "normal" : "bold",
                        fontFamily: fontFamily(),
                    }}>
                    .ts
                </Text>
                <Text
                    x={isRTL() ? 10 : 64} y={46}
                    width={340}
                    contain="width"
                    textAlign={isRTL() ? "right" : "left"}
                    style={{
                        fontSize: 18,
                        color: hexColor("#6b7280"),
                        fontFamily: fontFamily(),
                    }}>
                    {t("ts_desc")}
                </Text>

                {selected() === ".ts" && (
                    <View x={isRTL() ? 5 : 345} y={5} width={70} height={32} borderRadius={8}
                        style={{ color: hexColor("#0c3366") }}
                    >
                        <Text x={8} y={8} style={{
                            fontSize: 16,
                            color: hexColor("#60a5fa"),
                            fontFamily: fontFamily(),
                        }}>
                            {t("current")}
                        </Text>
                    </View>
                )}
            </View>

            {/* Option: .m3u8 */}
            <View
                ref={(el) => { m3u8El = el; props.m3u8Ref(el); }}
                y={228}
                x={isRTL() ? 450 : 0}
                style={optionStyle(".m3u8")}
                onUp={() => tsEl?.setFocus()}
                onLeft={() => props.onLeftFromOption()}
                onRight={() => props.onRightFromOption()}
                onEnter={() => choose(".m3u8")}
            >
                {/* Radio circle — mirrors in RTL */}
                <View
                    x={isRTL() ? 372 : 24} y={26} width={24} height={24} borderRadius={12}
                    style={{
                        color: hexColor("#374151"),
                        border: { width: 2, color: hexColor("#6b7280") },
                    }}
                >
                    {selected() === ".m3u8" && (
                        <View x={6} y={6} width={12} height={12} borderRadius={6}
                            style={{ color: hexColor("#ffffff") }}
                        />
                    )}
                </View>

                <Text
                    x={isRTL() ? 10 : 64} y={16}
                    width={340}
                    contain="width"
                    textAlign={isRTL() ? "right" : "left"}
                    style={{
                        fontSize: 26,
                        color: selected() === ".m3u8" ? hexColor("#f0f6fc") : hexColor("#9ca3af"),
                        fontWeight: isRTL() ? "normal" : "bold",
                        fontFamily: fontFamily(),
                    }}>
                    .m3u8
                </Text>
                <Text
                    x={isRTL() ? 10 : 64} y={46}
                    width={340}
                    contain="width"
                    textAlign={isRTL() ? "right" : "left"}
                    style={{
                        fontSize: 18,
                        color: hexColor("#6b7280"),
                        fontFamily: fontFamily(),
                    }}>
                    {t("m3u8_desc")}
                </Text>

                {selected() === ".m3u8" && (
                    <View x={isRTL() ? 5 : 345} y={5} width={70} height={32} borderRadius={8}
                        style={{ color: hexColor("#0c3366") }}
                    >
                        <Text x={8} y={8} style={{
                            fontSize: 16,
                            color: hexColor("#60a5fa"),
                            fontFamily: fontFamily(),
                        }}>
                            {t("current")}
                        </Text>
                    </View>
                )}
            </View>

        </View>
    );
};

export default StreamExtension;
